/**
 * RequestAccessModal — "Buy Test / Start Test" CTA modal.
 *
 * Pre-payments MVP path (Story 12-bis): collects name + email + optional
 * company + test of interest, POSTs to /api/catalogue/request-access, and
 * shows a confirmation message.
 *
 * Per docs/design.md: editorial density, modal overlay pattern.
 */

import { useRef, useEffect, useState, type ReactElement, type FormEvent } from 'react';
import { Button, Input } from '@/components/ui';
import { apiFetch } from '@/lib/api-client';

interface RequestAccessModalProps {
  /** Test the user is requesting access to */
  testId: string;
  testTitle: string;
  /** Called when the modal is dismissed */
  onClose: () => void;
}

async function submitRequestAccess(data: {
  name: string;
  email: string;
  company?: string;
  test_id: string;
}): Promise<{ ok: boolean; message?: string; error?: { code: string; message: string } }> {
  const res = await apiFetch('/api/catalogue/request-access', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(data),
  });
  const body = await res.json() as
    | { ok: true; data: { message: string } }
    | { ok: false; error: { code: string; message: string } };
  if (body.ok) return { ok: true, message: body.data.message };
  return { ok: false, error: body.error };
}

export function RequestAccessModal({
  testId,
  testTitle,
  onClose,
}: RequestAccessModalProps): ReactElement {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldError, setFieldError] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setFieldError({});
    setGeneralError('');
    setSubmitting(true);
    try {
      const result = await submitRequestAccess({
        name,
        email,
        company: company.trim() || undefined,
        test_id: testId,
      });
      if (result.ok) {
        setSubmitted(true);
        setSuccessMessage(result.message ?? 'Request received!');
      } else {
        if (result.error?.code === 'validation') {
          setFieldError({ [result.error.message.includes('email') ? 'email' : 'form']: result.error.message });
        } else {
          setGeneralError(result.error?.message ?? 'Something went wrong. Please try again.');
        }
      }
    } catch {
      setGeneralError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="request-access-title"
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ink/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={dialogRef}
        className="relative z-10 w-full max-w-lg rounded-lg border border-line bg-card p-8 shadow-xl mx-4"
      >
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={onClose}
          aria-label="Close dialog"
          className="absolute right-4 top-4 text-muted hover:text-ink"
        >
          ✕
        </Button>

        <p className="font-mono text-eyebrow font-medium uppercase text-muted mb-2">
          Request Access
        </p>

        <h2 id="request-access-title" className="font-sans text-h3 font-medium text-ink mb-1">
          Request Access
        </h2>
        <p className="font-sans text-small text-muted mb-6">
          {testTitle}
        </p>

        {submitted ? (
          <div role="status" className="text-center py-8">
            <p className="font-sans text-body text-success font-medium mb-2">Request sent!</p>
            <p className="font-sans text-small text-muted">{successMessage}</p>
            <Button
              variant="primary"
              type="button"
              onClick={onClose}
              className="mt-6"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={(e) => void handleSubmit(e)} noValidate>
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label htmlFor="ra-name" className="block font-sans text-small font-medium text-ink mb-1">
                  Full name <span aria-hidden className="text-error">*</span>
                </label>
                <Input
                  ref={firstInputRef}
                  id="ra-name"
                  type="text"
                  required
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-md border border-line-2 bg-bg px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
                {fieldError['name'] && (
                  <p role="alert" className="mt-1 font-sans text-small text-error">{fieldError['name']}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="ra-email" className="block font-sans text-small font-medium text-ink mb-1">
                  Email <span aria-hidden className="text-error">*</span>
                </label>
                <Input
                  id="ra-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-line-2 bg-bg px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
                {fieldError['email'] && (
                  <p role="alert" className="mt-1 font-sans text-small text-error">{fieldError['email']}</p>
                )}
              </div>

              {/* Company (optional) */}
              <div>
                <label htmlFor="ra-company" className="block font-sans text-small font-medium text-ink mb-1">
                  Company <span className="font-normal text-muted">(optional)</span>
                </label>
                <Input
                  id="ra-company"
                  type="text"
                  autoComplete="organization"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full rounded-md border border-line-2 bg-bg px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>

              {/* Test of interest (read-only display) */}
              <div>
                <p className="font-sans text-small font-medium text-ink mb-1">Test of interest</p>
                <p className="font-sans text-small text-muted">{testTitle}</p>
              </div>

              {generalError && (
                <p role="alert" className="font-sans text-small text-error">{generalError}</p>
              )}

              <Button
                variant="primary"
                type="submit"
                disabled={submitting}
                className="w-full"
              >
                {submitting ? 'Submitting…' : 'Request Access'}
              </Button>

              <p className="font-sans text-small text-muted-2 text-center">
                A Share Token will be issued within one business day.
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
